/*
 *  Copyright (c) Nexign, JSC, 2019.
 */

/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.0.0
 * @since 1.0.0
 */

import { PureComponent } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import ErrorPage from '../../../pages/ErrorPage/ErrorPage';
import { IRootReducer } from '../../../reducers/rootReducer';
import { icons } from '../../../theme';
import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import ICommonTranslation from '../../internationalization/types/common';
import { setInitAppError } from '../appActions';
import { makeSelectAppErrorCode, makeSelectAppHasInitError } from '../appSelectors';
import { ISetInitAppErrorPayload } from '../appTypes';
import App from './App';

interface IDispatchProps {
    setInitAppError: (payload: ISetInitAppErrorPayload) => void;
}

interface IConnectedProps {
    appErrorCode: string;
    hasInitError: boolean;
    translations: ICommonTranslation;
}

type SafeAppProps = IConnectedProps & IDispatchProps;

class SafeApp extends PureComponent<SafeAppProps> {

    /**
     * @description This lifecycle is invoked after an error has been thrown by a descendant component.
     * @param {Error} error The error that was thrown.
     * @param {React.ErrorInfo} errorInfo
     * An object with a componentStack key containing information about which component threw the error.
     */
    componentDidCatch(): void {
        this.props.setInitAppError({ hasInitError: true });
    }

    getErrorPageData() {
        const { appErrorCode, translations } = this.props;
        if (this.props.appErrorCode) {
            return {
                title: `${translations.error.ui.title} (${appErrorCode})`,
                text: translations.error.ui.text,
            };
        }
        return {
            title: translations.error.ui.title,
            text: translations.error.ui.text,
        }
    }

    render() {
        return (
            this.props.hasInitError
                ? <ErrorPage
                    src={icons.errorDeliveryImage3x}
                    {...this.getErrorPageData()}
                />
                : <App/>
        );
    }
}

const mapConnectToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    hasInitError: makeSelectAppHasInitError(),
    appErrorCode: makeSelectAppErrorCode(),
    translations: makeSelectTranslationCommon(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setInitAppError: (payload: ISetInitAppErrorPayload) => dispatch(setInitAppError(payload)),
});

export default connect(mapConnectToProps, mapDispatchToProps)(SafeApp);
